import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const plans = [
  {
    id: 'starter',
    name: 'スターター',
    nameEn: 'Starter',
    price: '50,000',
    unit: '円〜/月',
    tag: '',
    desc: 'スモールビジネス・個人事業主向けのエントリープラン。まずは相談から始めたい方に。',
    features: [
      '月1回の戦略ミーティング（90分）',
      'メール・チャットサポート（平日対応）',
      '月次レポート提供',
      '課題ヒアリング・初期診断',
      '業務改善アドバイス（基本）',
    ],
    notIncluded: [
      '広告運用代行',
      '空間デザイン対応',
      '専任担当者アサイン',
    ],
    highlight: false,
  },
  {
    id: 'standard',
    name: 'スタンダード',
    nameEn: 'Standard',
    price: '150,000',
    unit: '円〜/月',
    tag: '人気No.1',
    desc: '成長フェーズの中小企業・スタートアップに最適。コンサルと広告を組み合わせた定番プラン。',
    features: [
      '月2回の戦略ミーティング（各120分）',
      '経営・マーケティング戦略立案',
      'デジタル広告プランニング・運用',
      '月次KPIレポート＋改善提案',
      'メール・電話サポート（優先対応）',
      '専任コンサルタント1名',
    ],
    notIncluded: [
      '空間デザイン対応',
      '人材育成研修',
    ],
    highlight: true,
  },
  {
    id: 'premium',
    name: 'プレミアム',
    nameEn: 'Premium',
    price: '300,000',
    unit: '円〜/月',
    tag: '',
    desc: '本格的な事業拡大を目指す企業向け。全事業領域を横断したフルサポートプラン。',
    features: [
      '月4回の戦略ミーティング（無制限延長可）',
      '全事業領域の統合コンサルティング',
      '広告・PR全般の企画〜実行',
      '店舗・空間デザイン対応（別途見積）',
      '人材育成研修・コーチング',
      '専任チーム（コンサル＋クリエイター）',
      '24時間緊急対応窓口',
    ],
    notIncluded: [],
    highlight: false,
  },
];

const faq = [
  {
    q: '契約期間はどのくらいですか？',
    a: '最低契約期間は3ヶ月です。その後は1ヶ月単位で更新可能です。',
  },
  {
    q: 'プランの途中変更はできますか？',
    a: 'はい、月単位でプランのアップグレード・ダウングレードが可能です。変更は翌月から適用となります。',
  },
  {
    q: '料金に含まれないものはありますか？',
    a: '広告出稿費用（媒体費）、外注クリエイティブ費用、出張費などは別途ご負担いただきます。',
  },
  {
    q: 'まず試してみることはできますか？',
    a: '初回無料相談（60分）をご用意しています。お気軽にお問い合わせください。',
  },
];

export default function PlansPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Hero */}
      <section className="w-full bg-[#F5F2ED] pt-32 pb-16 px-6 md:px-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none select-none flex items-center justify-center">
          <p className="text-[200px] font-black text-[#0A0E1A] tracking-tighter whitespace-nowrap">PLANS</p>
        </div>
        <div className="relative z-10">
          <p className="text-xs tracking-[0.3em] text-[#C8501A] uppercase mb-4">Pricing Plans</p>
          <h1 className="text-4xl md:text-6xl font-black text-[#0A0E1A] leading-tight">
            料金プラン
          </h1>
          <p className="text-sm text-[#6B6560] mt-4 max-w-lg">
            ビジネスのフェーズや目標に合わせて、最適なプランをお選びください。<br />
            すべてのプランに初回無料相談が含まれます。
          </p>
        </div>
      </section>

      {/* Plans cards */}
      <section className="w-full py-16 md:py-24 px-6 md:px-16 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl overflow-hidden flex flex-col ${
                plan.highlight
                  ? 'bg-[#0A0E1A] text-white'
                  : 'bg-[#F5F2ED] text-[#0A0E1A]'
              }`}
            >
              {plan.tag && (
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-[#C8501A] text-white text-xs font-bold rounded-full whitespace-nowrap">
                    {plan.tag}
                  </span>
                </div>
              )}

              <div className="p-7 flex-1 flex flex-col">
                {/* Plan name */}
                <div className="mb-6">
                  <p className={`text-xs tracking-widest uppercase mb-1 ${plan.highlight ? 'text-[#C8501A]' : 'text-[#9C9490]'}`}>
                    {plan.nameEn}
                  </p>
                  <h3 className="text-xl font-black">{plan.name}</h3>
                </div>

                {/* Price */}
                <div className="flex items-end gap-1 mb-4">
                  <span className="text-4xl font-black">{plan.price}</span>
                  <span className={`text-xs mb-1.5 ${plan.highlight ? 'text-white/50' : 'text-[#9C9490]'}`}>{plan.unit}</span>
                </div>

                <p className={`text-xs leading-relaxed mb-6 ${plan.highlight ? 'text-white/60' : 'text-[#6B6560]'}`}>
                  {plan.desc}
                </p>

                {/* Divider */}
                <div className={`w-full h-px mb-6 ${plan.highlight ? 'bg-white/10' : 'bg-[#DDD9D3]'}`} />

                {/* Features */}
                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#C8501A] text-sm" />
                      </span>
                      <span className={`text-xs leading-relaxed ${plan.highlight ? 'text-white/80' : 'text-[#6B6560]'}`}>{f}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`ri-close-line text-sm ${plan.highlight ? 'text-white/30' : 'text-[#C8C4BC]'}`} />
                      </span>
                      <span className={`text-xs leading-relaxed line-through ${plan.highlight ? 'text-white/30' : 'text-[#C8C4BC]'}`}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`w-full py-3 rounded-full text-sm font-medium text-center transition-colors cursor-pointer whitespace-nowrap ${
                    plan.highlight
                      ? 'bg-[#C8501A] text-white hover:bg-[#A83E10]'
                      : 'bg-[#0A0E1A] text-white hover:bg-[#C8501A]'
                  }`}
                >
                  このプランで相談する →
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Custom plan note */}
        <div className="max-w-6xl mx-auto mt-8">
          <div className="bg-[#F5F2ED] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-[#0A0E1A] mb-1">カスタムプランも承ります</p>
              <p className="text-xs text-[#6B6560]">
                上記プランに含まれないご要望や、複数事業領域にわたる包括的なご支援はカスタム見積りをご用意します。
              </p>
            </div>
            <Link
              to="/contact"
              className="flex-shrink-0 px-6 py-2.5 bg-[#0A0E1A] text-white text-sm font-medium rounded-full hover:bg-[#C8501A] transition-colors cursor-pointer whitespace-nowrap"
            >
              カスタム見積り依頼 →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full py-16 md:py-20 px-6 md:px-16 bg-[#F5F2ED]">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs tracking-[0.3em] text-[#C8501A] uppercase mb-3 text-center">FAQ</p>
          <h2 className="text-2xl md:text-3xl font-black text-[#0A0E1A] text-center mb-10">よくあるご質問</h2>

          <div className="space-y-3">
            {faq.map((item, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-sm font-medium text-[#0A0E1A]">{item.q}</span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#C8501A]`} />
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-6 pb-5 text-xs text-[#6B6560] leading-relaxed">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
